import { useState } from "react";
import { X, Crown, Lightning, CircleNotch, GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

/**
 * LoginModal — full-screen tabbed Log-in / Sign-up sheet.
 *
 * Opens whenever a guest triggers an auth-gated action (like, comment,
 * apply, message, post…). Backdrop tap or the X button dismisses without
 * navigating. Successful auth closes the modal and lets the original
 * action re-fire against the now-authed session.
 *
 * Design blueprint reference: `/app/design_guidelines.json`
 * ("component_instructions.LoginModal").
 */
export default function LoginModal({ open, onClose, initialTab = "login", reason }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Shared form state — sign-up needs `name` + `username`, log-in doesn't.
  const [form, setForm] = useState({ email: "", password: "", name: "", username: "" });

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (tab === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.password, form.name, form.username);
      }
      onClose?.(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={() => onClose?.(false)}
    >
      <div className="absolute inset-0 backdrop-blur-xl bg-[#0a0a14]/85" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md card-sharp overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(20,15,10,0.98), rgba(8,5,2,0.98))",
          border: "1px solid #FFD700",
          boxShadow: "0 0 60px rgba(255,215,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <button
          data-testid="login-modal-close"
          aria-label="Close"
          onClick={() => onClose?.(false)}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground z-10"
        ><X size={16} /></button>

        <div className="relative p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] inline-flex items-center gap-2">
            <Crown size={12} weight="fill" /> HigherVision
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
            {tab === "login" ? "Welcome back." : "Join the crew."}
          </h2>
          {reason && (
            <p data-testid="login-modal-reason" className="text-[11px] text-muted-foreground mt-2 leading-snug">{reason}</p>
          )}

          {/* Tab strip */}
          <div className="mt-5 grid grid-cols-2 gap-1 border border-[#FFD700]/25" role="tablist">
            {[
              { key: "login", label: "Log in" },
              { key: "signup", label: "Sign up" },
            ].map((t) => (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={tab === t.key}
                data-testid={`login-modal-tab-${t.key}`}
                onClick={() => { setTab(t.key); setErr(""); }}
                className={`py-2 text-[11px] uppercase tracking-widest transition-colors ${
                  tab === t.key ? "bg-[#FFD700]/10 text-[#FFD700]" : "text-muted-foreground hover:text-foreground"
                }`}
              >{t.label}</button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-2.5">
            {tab === "signup" && (
              <>
                <input
                  data-testid="login-modal-name"
                  required
                  className="input-sharp w-full text-sm"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="login-modal-username"
                  required
                  className="input-sharp w-full text-sm"
                  placeholder="Username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.trim().toLowerCase() })}
                />
              </>
            )}
            <input
              data-testid="login-modal-email"
              required
              type="email"
              autoComplete="email"
              className="input-sharp w-full text-sm"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              data-testid="login-modal-password"
              required
              type="password"
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              className="input-sharp w-full text-sm"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            {err && (
              <div data-testid="login-modal-error" className="text-[11px] text-neon-pink leading-snug">{err}</div>
            )}
            <button
              type="submit"
              data-testid="login-modal-submit"
              disabled={busy}
              className="btn-neon w-full justify-center mt-1 disabled:opacity-60"
              style={{ backgroundColor: "#FFD700", color: "#0a0a14" }}
            >
              {busy ? <><CircleNotch size={13} className="animate-spin"/> Please wait…</> : <><Lightning size={13} weight="fill"/> {tab === "login" ? "Log in" : "Create account"}</>}
            </button>
          </form>

          <button
            type="button"
            data-testid="login-modal-continue-guest"
            onClick={() => onClose?.(false)}
            className="w-full mt-3 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Continue as guest →
          </button>
        </div>
      </div>
    </div>
  );
}
